/**
 * The two surfaces that sit inside the panel's own box: the input-capture layer
 * and the desktop native view.
 *
 * Extracted from `BrowserSurfaceHost` when that file crossed the 300-line god
 * -file line in `.agents/rules/frontend-architecture.md` ("extract before
 * adding"). It is also the natural seam: this pair is the only place that still
 * mounts `InteractiveBrowserSurface`, whose `frameDataUrl` is now the deleted
 * legacy screencast picture and arrives `undefined` for every interactive
 * session. The component keeps the prop because annotation mode still paints a
 * still through it.
 *
 * @module BrowserInteractiveCanvasSurface
 */
import type {
  BrowserDisplaySession,
  BrowserViewport,
  DesktopNativeBrowserResult,
} from "@cmmd-forge/contracts";
import type { ComponentProps } from "react";

import { BrowserCanvas } from "../../components/browser/BrowserCanvas";
import { DesktopNativeBrowserSurface } from "../../components/browser/DesktopNativeBrowserSurface";
import { InteractiveBrowserSurface } from "../../components/browser/InteractiveBrowserSurface";

type BrowserCanvasProps = ComponentProps<typeof BrowserCanvas>;

export interface BrowserInteractiveCanvasSurfaceProps {
  readonly activeTabUrl: string | null;
  readonly browserDisplay: BrowserDisplaySession | undefined;
  readonly browserId: ComponentProps<typeof DesktopNativeBrowserSurface>["browserId"];
  readonly emptyLabel: string;
  readonly frameDataUrl: string | undefined;
  readonly interactionEnabled: boolean;
  readonly nativeEmbeddedDisplayActive: boolean;
  readonly onContainerResize: NonNullable<BrowserCanvasProps["onContainerResize"]>;
  readonly onKeyboardCommand: ComponentProps<typeof InteractiveBrowserSurface>["onKeyboardCommand"];
  readonly onNativeError: (error: unknown) => void;
  readonly onNativeResult: (result: DesktopNativeBrowserResult) => void;
  readonly onPointerCommand: ComponentProps<typeof InteractiveBrowserSurface>["onPointerCommand"];
  readonly sessionId: ComponentProps<typeof DesktopNativeBrowserSurface>["sessionId"];
  readonly tabId: ComponentProps<typeof DesktopNativeBrowserSurface>["tabId"];
  readonly viewport: BrowserViewport | undefined;
}

export function BrowserInteractiveCanvasSurface(props: BrowserInteractiveCanvasSurfaceProps) {
  return (
    <>
      <InteractiveBrowserSurface
        tabId={props.tabId}
        frameDataUrl={props.frameDataUrl}
        viewport={props.viewport}
        display={props.browserDisplay}
        interactionEnabled={props.interactionEnabled}
        selectionEnabled={false}
        emptyLabel={props.emptyLabel}
        onPointerCommand={props.onPointerCommand}
        onKeyboardCommand={props.onKeyboardCommand}
        onContainerResize={props.onContainerResize}
      />
      <DesktopNativeBrowserSurface
        active={props.nativeEmbeddedDisplayActive}
        browserId={props.browserId}
        sessionId={props.sessionId}
        tabId={props.tabId}
        url={props.activeTabUrl}
        onError={props.onNativeError}
        onResult={props.onNativeResult}
      />
    </>
  );
}
